// Estimator — thin wrapper that lets the planning team gather quick params
// then deep-links into the existing Combined Calculator to do the real work.
// We never reimplement quoting logic (single source of truth lives in
// /app/frontend/src/pages/calculators/CombinedCalculator.jsx).
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { SectionHeader } from "./pccHelpers";
import { ArrowRight, Calculator } from "lucide-react";

export default function PccEstimator() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", count: 10, complexity: "mid", style: "Exterior", revisions: 2, buffer: 15, team: 3, format: "Still" });

  const baseDays = { low: 1.5, mid: 2.5, high: 4 }[form.complexity];
  const styleMult = { Exterior: 1, Interior: 1.1, Aerial: 1.3, Animation: 1.6, Mixed: 1.2 }[form.style] || 1;
  const totalDays = Math.ceil((Number(form.count) * baseDays * styleMult * (1 + Number(form.revisions) * 0.15)) * (1 + Number(form.buffer) / 100) / Math.max(1, Number(form.team)));

  return (
    <div className="space-y-5" data-testid="pcc-estimator">
      <div>
        <h2 className="text-xl font-light text-white">Estimator</h2>
        <p className="text-white/55 text-sm mt-1">Rough timeline & resource estimate. For binding pricing, push the inputs into the Combined Calculator.</p>
      </div>
      <div className="grid gap-5" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden">
          <SectionHeader dot="#B96A43" title="Project Parameters" />
          <div className="p-5 space-y-3">
            <div><Label className="text-[11px] text-white/55">Project / Client Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Edge House — Tower B" className="bg-black/30 border-white/10 text-white mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[11px] text-white/55">Deliverable Count</Label><Input type="number" value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
              <div><Label className="text-[11px] text-white/55">Complexity</Label>
                <Select value={form.complexity} onValueChange={(v) => setForm({ ...form, complexity: v })}><SelectTrigger className="bg-black/30 border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="mid">Mid</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[11px] text-white/55">Style Type</Label>
                <Select value={form.style} onValueChange={(v) => setForm({ ...form, style: v })}><SelectTrigger className="bg-black/30 border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Exterior", "Interior", "Aerial", "Animation", "Mixed"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-[11px] text-white/55">Revision Rounds</Label><Input type="number" min="1" max="5" value={form.revisions} onChange={(e) => setForm({ ...form, revisions: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-[11px] text-white/55">Buffer %</Label><Input type="number" min="5" max="40" value={form.buffer} onChange={(e) => setForm({ ...form, buffer: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
              <div><Label className="text-[11px] text-white/55">Team Size</Label><Input type="number" min="1" max="10" value={form.team} onChange={(e) => setForm({ ...form, team: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
              <div><Label className="text-[11px] text-white/55">Output</Label>
                <Select value={form.format} onValueChange={(v) => setForm({ ...form, format: v })}><SelectTrigger className="bg-black/30 border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Still", "Animation", "Both"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={() => navigate(`/sales/calculators/combined?prefill_name=${encodeURIComponent(form.name)}&prefill_count=${form.count}&prefill_style=${form.style}`)} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="pcc-est-push">
                Push to Combined Calculator <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
        </Card>

        <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden">
          <SectionHeader dot="#2E7D55" title="Estimate Results" />
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center"><Calculator className="w-5 h-5 text-emerald-300" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-white/40">Estimated Calendar Days</div>
                <div className="text-3xl font-light text-white tabular-nums">{totalDays}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-white/[0.06]">
              <div><div className="text-[10px] uppercase tracking-wider text-white/40">Base Days/Unit</div><div className="text-base font-mono">{baseDays.toFixed(1)}d</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-white/40">Style Multiplier</div><div className="text-base font-mono">{styleMult}×</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-white/40">Revisions Loading</div><div className="text-base font-mono">+{Number(form.revisions) * 15}%</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-white/40">Buffer</div><div className="text-base font-mono">+{form.buffer}%</div></div>
            </div>
            <div className="text-[11px] text-white/45 mt-3">Rough estimate only. Pricing, payment terms and deliverable mix are configured in the Combined Calculator.</div>
          </div>
        </Card>
      </div>
    </div>
  );
}
